/// <amd-module name="TFS/Admin/Views/ProjectOverview/ProjectAdmins" />

import * as React from "react";

import AdminResources = require("TFS/Admin/Views/ProjectOverview/Resources");

import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl, IdentityImageSize } from "VSS/Platform/Identity";
import { VssComponent } from "VSS/Platform/Layout";

import { IPanelSelectChange, UserMessageSeverity } from "TFS/Admin/Common/Contracts/SelectUserPanel";
import { IIdentity } from "VSSUI/IdentityPicker";

import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { ICustomIdentityRef } from "TFS/Admin/Common/Contracts/CustomIdentityRef";
import { ISelectUserPanelProps } from "TFS/Admin/Common/Controls/SelectUserPanel/SelectUserPanel.Props";
import { IListUsersAndManageProps } from "TFS/Admin/Common/Controls/ListUsersAndManage/ListUsersAndManage.props";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { GraphUserOriginIdCreationContext } from "VSS/Platform/Contracts/Graph";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IPersonaProvider } from "VSSUI/Persona";

export interface IProjectAdminsProps {
    orgName: string;
    orgSettingsUrl: string;
    totalIdentityCount: number;
    identities: IGraphViewModel[];
    groupDescriptor: string;
    canAddMemberToAdminGroup: boolean;
}

export interface IGraphViewModel {
    identityId: string;
    displayName: string;
    mailAddress?: string;
    scope?: string;
    subjectKind: string;
    descriptor: string;
}

export interface State {
    showAddAdminPanel?: boolean;
    success?: string;
    error?: string;
}

export class ProjectAdminsComponent extends VssComponent<IProjectAdminsProps, State> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private personaProvider: IPersonaProvider;

    private _adminsList: IObservableArray<ICustomIdentityRef>;
    public readonly MAX_DISPLAY_CNT: number = 3;

    public constructor(props: IProjectAdminsProps, context?: any) {
        super(props, context);

        this.state = {};

        this._adminsList = new ObservableArray([]);
        this.personaProvider = new PersonaDetailsProvider(this.context.pageContext);
        this._populateProjectAdmins();
    }

    public render(): JSX.Element {
        return (
            <>
                <div className="project-admins flex-column">
                    {this.state.success && (
                        <MessageBar
                            severity={MessageBarSeverity.Success}
                            onDismiss={() => this.setState({ success: undefined })}
                            className="success-message-bar"
                        >
                            {this.state.success}
                        </MessageBar>
                    )}
                    {this.state.error && (
                        <MessageBar
                            severity={MessageBarSeverity.Error}
                            onDismiss={() => this.setState({ error: undefined })}
                            className="failure-message-bar"
                        >
                            {this.state.error}
                        </MessageBar>
                    )}

                    <WrappedComponent<IListUsersAndManageProps>
                        dependencies={["ms.vss-admin-web.admin-common"]}
                        wrappedType={"ms.vss-admin-web.list-users-and-manage-component"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            title: AdminResources.ProjectAdministratorsHeading,
                            identities: this._adminsList,
                            maxIdentitiesToDisplayCount: this.MAX_DISPLAY_CNT,
                            canAddMember: this.props.canAddMemberToAdminGroup,
                            addButtonLabel: AdminResources.AddAdminButtonLabel,
                            onAddMember: this._onAddAdminClick
                        }}
                    />

                    {this.state.showAddAdminPanel && (
                        <WrappedComponent<ISelectUserPanelProps>
                            dependencies={["ms.vss-admin-web.admin-common"]}
                            wrappedType={"ms.vss-admin-web.select-user-panel"}
                            wrappedProps={{
                                pageContext: this.context.pageContext,
                                onCancel: this._onCancelAddAdmin,
                                onSelectUser: this._onAddAdmin,
                                panelTitle: AdminResources.AddAdminPanelTitle,
                                actionButtonText: AdminResources.AddAdminPanelActionButtonText,
                                identityTypesToLookup: ["user", "group", "servicePrincipal"],
                                onUserChange: this._onAdminChange,
                                selectUserPlaceHolderText: AdminResources.SearchUsersOrGroups,
                                selectUserEditPlaceHolderText: AdminResources.SearchUsersOrGroups
                            }}
                        />
                    )}
                </div>
            </>
        );
    }

    private _populateProjectAdmins(): void {
        this.props.identities.forEach((identity: IGraphViewModel) => {
            const name = identity.displayName;
            const emailOrScope = identity.subjectKind == "user" ? identity.mailAddress : identity.scope;

            const imageURL = getIdentityImageUrl(this.context.pageContext, { id: identity.identityId } as IdentityRef, IdentityImageSize.medium);

            this._adminsList.push({
                id: identity.identityId,
                imageUrl: imageURL,
                displayName: name,
                preferredEmailAddress: emailOrScope!,
                isUser: identity.subjectKind === "user",
                descriptor: identity.descriptor
            });
        });
    }

    private _onAdminChange = (selectedUser?: IIdentity): IPanelSelectChange => {
        if (!selectedUser) {
            return {
                hasError: true,
                errorComponent: <span>{AdminResources.AddAdminInvalidSelection}</span>,
                errorTextType: UserMessageSeverity.Error,
                enableActionButton: false
            };
        } else if (!selectedUser.localId) {
            return {
                hasError: true,
                errorComponent: (
                    <span>
                        {StringUtils.format(AdminResources.AddNonMemberWarning, selectedUser.entityType.toLocaleLowerCase(), this.props.orgName)}
                    </span>
                ),
                errorTextType: UserMessageSeverity.Warning,
                enableActionButton: true
            };
        } else if (this._adminsList.value.some(e => e.descriptor === selectedUser.subjectDescriptor)) {
            return {
                hasError: true,
                errorComponent: (
                    <span>
                        {StringUtils.format(
                            AdminResources.AddAdminExists,
                            selectedUser!.entityType.toLocaleLowerCase() || AdminResources.defaultUserLabel
                        )}
                    </span>
                ),
                errorTextType: UserMessageSeverity.Warning,
                enableActionButton: true
            };
        }

        return { hasError: false, enableActionButton: true };
    };

    private _onAddAdmin = async (selectedUser?: IIdentity) => {
        this.setState({ success: undefined, error: undefined });
        if (
            this._adminsList.length > 0 &&
            this._adminsList.value.some(arr => {
                return arr.descriptor === selectedUser!.subjectDescriptor;
            })
        ) {
            this.setAddAdminPanelState(false, undefined, undefined);
            return;
        }

        if (selectedUser!.localId) {
            this.addMemberToGroup(selectedUser!);
        } else if (selectedUser!.entityType.toLowerCase() === "user") {
            this.createUser(selectedUser!);
        } else {
            this.createGroup(selectedUser!);
        }
    };

    private addMemberToGroup = async (selectedUser: IIdentity) => {
        const actionTelemetryLogger = this._startActionCapture("Add Project Administrator Member To Group", {});
        const restClient = this.getGraphClient(this.context);
        restClient.addMembership(selectedUser!.subjectDescriptor!, this.props.groupDescriptor).then(
            async opref => {
                await this.addProjectAdmin(selectedUser!);
                this.setAddAdminPanelState(false, StringUtils.format(AdminResources.AddAdminSuccessMessage, selectedUser!.displayName), undefined);
            },
            error => {
                this.setAddAdminPanelState(false, undefined, error.message || String(error));
                actionTelemetryLogger.captureError(error.message || String(error));
            }
        );
    };

    private createUser = async (selectedUser: IIdentity) => {
        const actionTelemetryLogger = this._startActionCapture("Add Project Administrator", {});

        const restClient = this.getGraphClient(this.context);
        restClient
            .createUser({ storageKey: "", originId: selectedUser.originId } as GraphUserOriginIdCreationContext, [this.props.groupDescriptor])
            .then(
                async opref => {
                    await this.addProjectAdmin(opref);
                    this.setAddAdminPanelState(false, StringUtils.format(AdminResources.AddAdminSuccessMessage, opref!.displayName), undefined);
                },
                reason => {
                    this.setAddAdminPanelState(false, undefined, reason.message || String(reason));
                    actionTelemetryLogger.captureError(reason.message || String(reason));
                }
            );
    };

    private createGroup = async (selectedUser: IIdentity) => {
        const actionTelemetryLogger = this._startActionCapture("Add Project Administrator Create Group", {});

        const restClient = this.getGraphClient(this.context);
        restClient
            .createGroup({ storageKey: "", originId: selectedUser.originId } as GraphUserOriginIdCreationContext, "", [this.props.groupDescriptor])
            .then(
                async opref => {
                    await this.addProjectAdmin(opref);
                    this.setAddAdminPanelState(false, StringUtils.format(AdminResources.AddAdminSuccessMessage, opref!.displayName), undefined);
                },
                reason => {
                    this.setAddAdminPanelState(false, undefined, reason.message || String(reason));
                    actionTelemetryLogger.captureError(reason.message || String(reason));
                }
            );
    };

    private setAddAdminPanelState(showAddAdminPanel: boolean, successMsg: string | undefined, errorMsg: string | undefined) {
        this.setState({
            showAddAdminPanel: showAddAdminPanel,
            success: successMsg,
            error: errorMsg
        });
    }

    private constructAdminObject = (entity: any): ICustomIdentityRef => {
        // entity has type "localId" which means it's an instance of IIdentity
        if ("localId" in entity) {
            return {
                displayName: entity.displayName!,
                preferredEmailAddress: entity.entityType.toLowerCase() == "user" ? entity.mail : entity.scopeName,
                imageUrl: this.getImageUrl(entity),
                id: entity.localId!,
                isUser: entity.entityType.toLowerCase() === "user",
                descriptor: entity.subjectDescriptor
            };
        } else {
            // Else it's a new non-member object (IGraphUser or IGraphGroup).
            return {
                displayName: entity.displayName,
                preferredEmailAddress: entity.subjectKind.toLowerCase() == "user" ? entity.mailAddress : entity.scopeName,
                imageUrl: this.getImageUrl(entity),
                isUser: entity.subjectKind.toLowerCase() === "user",
                id: "",
                descriptor: entity.descriptor
            };
        }
    };

    private getImageUrl = (entity: any): string => {
        // entity has type "localId" which means it's an instance of IIdentity
        if ("localId" in entity) {
            return getIdentityImageUrl(this.context.pageContext, entity.localId!, IdentityImageSize.medium);
        } else if ("_links" in entity) {
            // Entity is an instance of IGraphUser
            if (entity._links.hasOwnProperty("avatar")) {
                return entity._links.avatar.href;
            }
        }

        // By default - return team.svg for groups.
        return getAssetLocation(this.context.pageContext, "ms.vss-admin-web/admin-common-content/team.svg");
    };

    private addProjectAdmin = async (entity: any): Promise<void> => {
        let index = 0;

        const adminEntity: ICustomIdentityRef = this.constructAdminObject(entity);
        // Sort at insertion when adding project admins
        if (this._adminsList.length == 0) {
            this._adminsList.push(adminEntity);
        } else {
            while (index < this._adminsList.length && this._adminsList.value[index].displayName.localeCompare(entity.displayName!, navigator.language) < 0) {
                index++;
            }

            this._adminsList.splice(index, 0, adminEntity);
        }
    };

    private _onCancelAddAdmin = () => {
        this.setState({ showAddAdminPanel: false });
    };

    private _onAddAdminClick = (): void => {
        this.setState({ showAddAdminPanel: true });
    };

    private getGraphClient(context: IVssComponentContext) {
        return context.pageContext.getRestClient<IGraphRestClient>("IGraphRestClient");
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.ProjectOverviewAdmins, Feature.ProjectOverviewAdmins, action, properties);
    };
}
